import { Component, Prop, State, h, Watch, Element } from '@stencil/core';
import { htmlSanitizer } from '../../utils/html-sanitizer';

// Types for props
type CardStyle = 'default' | 'highlighted';
type ImageAspectRatio = '1:1' | '2:1';
type TextColor = 'light' | 'dark';
type MediaType = 'image' | 'video' | 'none';
type CardSize = 'short' | 'long';

export interface BentoGridCardData {
  type: string;
  size: CardSize;
  eyebrow: string;
  headline: string;
  description: string;
  cardStyle?: CardStyle;
  imageAspectRatio?: ImageAspectRatio;
  backgroundCover?: 'half' | 'full';
  textColor?: TextColor;
  buttonText?: string;
  buttonUrl?: string;
  mediaType?: MediaType;
  imageUrl?: string;
  imageAltText?: string;
  videoUrl?: string;
  imagetype?: 'small' | 'large';
  autoplay?: 'looped' | 'single';
  cardGrad?: string;
}

export interface BentoGridData {
  theme?: 'light' | 'dark';
  cards: BentoGridCardData[];
  cardFlip?: 'yes' | 'no';
}

@Component({
  tag: 'vs-bento-grid',
  styleUrl: 'vs-bento-grid.css',
  shadow: false,
})
export class VsBentoGrid {
  @Element() el: HTMLElement;

  @Prop() data: string = '';
  @Prop() titleBlock: string;

  @State() parsedData: BentoGridData | null = null;
  @State() loading: boolean = true;
  @State() windowWidth: number = window.innerWidth;

  @Watch('data')
  watchDataHandler(newValue: string) {
    this.parseData(newValue);
  }

  componentWillLoad() {
    this.parseData(this.data);
    // Add resize event listener
    window.addEventListener('resize', this.handleResize);
  }

  disconnectedCallback() {
    // Remove resize event listener when component is destroyed
    window.removeEventListener('resize', this.handleResize);
  }

  private handleResize = () => {
    // Update window width state to force re-render
    this.windowWidth = window.innerWidth;
  };

  private parseData(dataString: string) {
    try {
      if (dataString && dataString.trim()) {
        const rawData = JSON.parse(dataString);

        // Check if data has cards array or individual card objects
        if (rawData.cards && Array.isArray(rawData.cards)) {
          this.parsedData = rawData;
          this.titleBlock = JSON.stringify(rawData['title-block']);
        } else {
          // Convert individual card objects to cards array
          const cards: BentoGridCardData[] = [];
          Object.keys(rawData).forEach(key => {
            if (key.startsWith('bento-grid-card')) {
              const cardData = rawData[key];
              cards.push({
                type: 'card',
                size: 'short',
                eyebrow: cardData.eyebrow || '',
                headline: cardData.headline || '',
                description: cardData.description || '',
                cardStyle: cardData.cardStyle || 'default',
                backgroundCover: cardData.backgroundCover || 'full',
                textColor: cardData.textColor || 'light',
                mediaType: cardData.mediaType || 'image',
                imageUrl: cardData.imageUrl || '',
                buttonText: cardData.cta?.title || '',
                buttonUrl: cardData.cta?.link || '',
                imagetype: cardData.imagetype || '',
                autoplay: cardData.autoplay || '',
                cardGrad: cardData.cardGrad || '',
              });
            }
          });

          this.parsedData = {
            theme: rawData.theme || 'light',
            cards,
          };
        }

        this.loading = false;
      } else {
        this.parsedData = null;
        this.loading = false;
      }
    } catch (error) {
      console.error('Error parsing bento grid data:', error);
      this.parsedData = null;
      this.loading = false;
    }
  }

  private handleButtonClick = (event: Event, card: BentoGridCardData) => {
    event.preventDefault();
    if (card.buttonUrl) {
      window.open(card.buttonUrl, '_self');
    }
  };

  private renderCard(card: BentoGridCardData, index: number) {
    const {
      eyebrow = '',
      headline = '',
      description = '',
      cardStyle = 'default',
      backgroundCover = 'full',
      textColor = 'light',
      buttonText = '',
      mediaType = 'none',
      imageUrl = '',
      videoUrl = '',
      imagetype = 'large',
      autoplay = 'single',
    } = card;

    // Common background style for image
    const cardStyleObj: any = {};
    const mediaPath = imageUrl || '';

    if (mediaPath && mediaType !== 'video') {
      // Apply background image
      if (card.cardGrad === 'true') {
        // Apply predefined linear gradient when cardGrad is true
        cardStyleObj.background = `linear-gradient(360deg, rgba(0, 0, 0, 0) 36.43%, rgba(0, 0, 0, 0.4) 100%), url('${mediaPath}')`;
      } else {
        cardStyleObj.background = `url('${mediaPath}')`;
      }

      if (backgroundCover === 'half') {
        cardStyleObj.backgroundSize = '100% 60%';
        // Update to center-lock images horizontally
        cardStyleObj.backgroundPosition = cardStyle === 'default' ? 'center bottom' : 'center top';
        if (imagetype === 'small') {
          // Update from 'left 15% top 11%' to center horizontally
          cardStyleObj.backgroundPosition = 'center top 11%';
          cardStyleObj.backgroundSize = '37% 28%';
          cardStyleObj.backgroundRepeat = 'no-repeat';
          // Remove height: auto to ensure fixed height on mobile
          if (this.windowWidth > 768) {
            cardStyleObj.height = 'auto';
          }
        }
      } else {
        cardStyleObj.backgroundSize = 'cover';
        // Add center positioning for full cover images
        cardStyleObj.backgroundPosition = 'center center';
      }
      cardStyleObj.backgroundRepeat = 'no-repeat';
      cardStyleObj.backgroundColor = '#EBECF1';
      cardStyleObj.color = textColor === 'light' ? 'white' : 'black';
    }

    // Video style
    let videoStyle: any = {
      position: 'absolute',
      width: '100%',
      objectFit: 'cover',
      left: '0',
      zIndex: '1',
      backgroundColor: '#EBECF1',
      height: '100%',
      // Add center positioning for videos
      objectPosition: 'center center',
    };

    if (backgroundCover === 'half') {
      if (this.windowWidth > 768) {
        videoStyle.height = '60%';
      } else {
        videoStyle.height = 'auto';
      }
      videoStyle.top = cardStyle === 'default' ? 'auto' : '0';
      videoStyle.bottom = cardStyle === 'default' ? '0' : 'auto';
    } else {
      videoStyle.height = '100%';
      videoStyle.top = '0';
      videoStyle.bottom = 'auto';
    }

    const contentStyle: any = {
      zIndex: '2',
    };

    // Apply consistent positioning for all cards with media (images or videos)
    if (mediaPath || mediaType === 'video') {
      contentStyle.position = 'absolute';
      // Use percentage values that match the padding of cards without media
      contentStyle.left = '9%'; // Equivalent to 4.75rem padding
      contentStyle.right = '9%'; // Equivalent to 4.75rem padding

      if (backgroundCover === 'half') {
        // For half-cover images, position based on card style
        contentStyle[cardStyle === 'default' ? 'top' : 'bottom'] = '7%';
      } else {
        // For full cover images and videos, position content at the top
        contentStyle.top = '7%'; // Equivalent to 4.75rem padding
      }
    }

    return (
      <div
        class={`bento-card ${mediaPath ? 'has-background-image' : ''} ${mediaType === 'video' ? 'has-video' : ''}`}
        style={cardStyleObj}
        key={index}
        tabindex="0"
        role="article"
        aria-labelledby={`bento-card-headline-${index}`}
      >
        {/* Render video background if mediaType is 'video' */}
        {mediaType === 'video' && videoUrl && (
          <video
            class="bento-card-video-bg"
            src={videoUrl || mediaPath}
            autoplay
            muted
            playsinline
            style={videoStyle}
            loop={autoplay === 'looped'}
            aria-hidden="true" // Hide from screen readers as it's decorative
          />
        )}

        {mediaType === 'video' && (videoUrl || mediaPath) && cardStyle === 'highlighted' && (
          <div aria-hidden="true">
            <div class="bento-card-video-gradient-horizontal-highlighted" />
            <div class="bento-card-video-gradient-vertical-highlighted" />
            {/* Add left-side gradient for balanced masking */}
            <div class="bento-card-video-gradient-vertical-highlighted-left" />
          </div>
        )}

        {mediaType === 'video' && (videoUrl || mediaPath) && cardStyle === 'default' && (
          <div aria-hidden="true">
            <div class="bento-card-video-gradient-horizontal-default" />
            <div class="bento-card-video-gradient-vertical-default" />
            {/* Add left-side gradient for balanced masking */}
            <div class="bento-card-video-gradient-vertical-default-left" />
          </div>
        )}

        {/* For background images, add hidden alt text for screen readers */}
        {mediaPath && mediaType !== 'video' && (
          <span class="vs-sr-only">{card.imageAltText || 'Background image'}</span>
        )}

        <div class="bento-card-content" style={contentStyle}>
          <div class="bento-card-text">
            {eyebrow && (
              <div class="bento-card-eyebrow-container">
                {/* Visible eyebrow text with aria-hidden */}
                <span class="bento-card-eyebrow" aria-hidden="true">
                  {eyebrow}
                </span>
                {/* Screen reader version inside the headline for proper context */}
                <span class="vs-sr-only">{eyebrow}</span>
              </div>
            )}
            <h3 id={`bento-card-headline-${index}`} class="bento-card-headline">
              {headline}
            </h3>
            <div class="bento-card-description">
              <p innerHTML={htmlSanitizer(description)}></p>
            </div>
          </div>

          {buttonText && (
            <div class="button-group">
              <vsr-button-v2
                href={card.buttonUrl || '#'}
                variant="secondary"
                themeOverride={textColor == 'light' ? 'dark' : 'light'}
                state={card.buttonUrl ? 'active' : 'inactive'}
                onClick={event => this.handleButtonClick(event, card)}
                contextDescription={headline}
              >
                {buttonText}
              </vsr-button-v2>
            </div>
          )}
        </div>
      </div>
    );
  }

  render() {
    if (this.loading) {
      return (
        <div class="bentogrid-container">
          <div class="loading-skeleton" aria-live="polite">
            Loading...
          </div>
        </div>
      );
    }

    if (!this.parsedData) {
      return (
        <div class="bentogrid-container">
          <div class="error-message" role="alert">
            No data provided for Bento Grid
          </div>
        </div>
      );
    }

    const { theme, cards = [], cardFlip } = this.parsedData;

    const themeClass = theme === 'light' ? 'theme-light' : 'theme-dark';

    // Limit to 4 cards maximum
    const displayCards = cards.slice(0, 4);

    // Create a reordered array for mobile view
    // Order: first left top card, then right first card, then left second card, then right second card
    const mobileOrderCards = [
      displayCards[0], // left top card (index 0)
      displayCards[2], // right first card (index 2)
      displayCards[1], // left second card (index 1)
      displayCards[3], // right second card (index 3)
    ].filter(Boolean); // Filter out any undefined cards if less than 4 cards are provided

    return (
      <div class={`bentogrid-container ${themeClass}`}>
        <vsr-title
          title-block={this.titleBlock}
          is-padding-horizontal="false"
          theme={theme}
        ></vsr-title>
        <div class={`bentogrid ${cardFlip == 'yes' ? 'flip' : ''}`}>
          <div class="bentogrid-column column-left">
            {displayCards.slice(0, 2).map((card, index) => this.renderCard(card, index))}
          </div>
          <div class="bentogrid-column column-right">
            {displayCards.slice(2, 4).map((card, index) => this.renderCard(card, index + 2))}
          </div>
        </div>

        {/* Mobile layout - single column with reordered cards */}
        <div class="bentogrid-mobile" role="region">
          {mobileOrderCards.map((card, index) => this.renderCard(card, index))}
        </div>
      </div>
    );
  }
}
